/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED, } from "../../../../share/util/api/polling";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';

/** 各フェーズ定義 */
const PHASE = {
  WAIT: 'wait',
  SUCCESS: 'success',
  ERROR: 'error',
};

export default {
  name: 'requirement152',
  data() {
    return {
      phase: PHASE.WAIT,
      isLoading: false,
      isDispose: false,
      systemId: '',
      modal_requirement_152: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     * @returns {string} ヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.WAIT:
        default:
          return 'secondary';
      }
    },

    /**
     * 表示ヘッダー文字列判定
     * @returns {string} ヘッダー文字列
     */
    headerTitle() {
      // 待機表示
      const confirmationTitle = 'message.common_title_confirmation';
      // 停止表示
      const successTitle = 'message.common_title_success';
      // エラー表示
      const errorTitle = 'message.common_title_error';
      const titleMap = {
        [PHASE.WAIT]: confirmationTitle,
        [PHASE.SUCCESS]: successTitle,
        [PHASE.ERROR]: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * [キャンセル]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.WAIT]: true,
        [PHASE.SUCCESS]: false,
        [PHASE.ERROR]: false,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * 作業サポート表示メッセージ判定
     * @returns {string} メッセージ
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.WAIT]: `${this.i18nWsKey}.REQ_WS_152_TXT_1`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_152_TXT_2`,
        [PHASE.ERROR]: `${this.i18nWsKey}.REQ_WS_152_TXT_3`,
      };
      return this.$t(messageMap[this.phase]);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_152 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_152 = false;
    },

    /**
   * API 通信で共通バリデーションでエラーが発生した際のコールバック
   * @param {object} result バリデーション結果
   */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // 呼び元でエラー処理を実行する
        (result) => { result; },
      );
    },

    /** 実行処理 */
    async executeProcess() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          await this.showUnexpectedError();
          return;
        }

        this.isDispose = response.data?.user_data?.is_dispose;
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            await this.exitProcess();
            this.phase = PHASE.SUCCESS;
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** Dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // Dispose処理（バックエンドAPI）
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose',
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * exit処理
     */
    async exit() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        // 結果に関わらずdispose処理を実施する
        await this.dispose();
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exitProcess() {
      if (this.isDispose) {
        // 現在セッションがdefaultSession
        await this.dispose();
      } else {
        // 現在セッションがdefaultSessionでない
        await this.exit();
      }
    },

    /** エラー発生時の表示 */
    async showUnexpectedError() {
      // 異常終了：エラー表示
      await this.exitProcess();
      this.phase = PHASE.ERROR;
    },

    /** OKクリック時の処理 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.WAIT:
          await this.executeProcess();
          break;
        case PHASE.SUCCESS:
        case PHASE.ERROR:
          this.closeWizard();
          break;
        default:
          break;
      }
    },
  },

  mounted() {
    this.init();
  },
};